import { useCallback } from 'react'

import { Download, Navigation, Schedule } from '@mui/icons-material'
import { Box, Link, Typography } from '@mui/material'

import { DropoffDetailsModalProps } from '../../FulfillmentOrderAccordion.types'
import styles from './DropoffDetailsModal.styles'

const DropoffDetailsModal = (props: DropoffDetailsModalProps) => {
  const { uspsLabelUrl, uspsLocationName, uspsLocationAddress, uspsLocationHours } = props

  const handleGetDirections = useCallback(() => {
    if (!uspsLocationAddress) return

    const encodedAddress = encodeURIComponent(uspsLocationAddress.replace(/\n/g, ', '))

    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank', 'noopener,noreferrer')
  }, [uspsLocationAddress])

  const renderStep1Content = () => (
    <Box sx={styles.stepActionBlock}>
      {uspsLabelUrl ? (
        <Link href={uspsLabelUrl} target="_blank" rel="noopener noreferrer" underline="none" sx={styles.downloadButton}>
          <Download sx={styles.downloadIcon} />

          <Typography sx={styles.downloadText} component="span">
            Download USPS Label (PDF)
          </Typography>
        </Link>
      ) : (
        <Box sx={styles.downloadButtonDisabled}>
          <Download sx={styles.downloadIconDisabled} />
          <Typography sx={styles.downloadTextDisabled}>Label not yet available</Typography>
        </Box>
      )}

      <Typography sx={styles.labelHintText}>Print on standard letter-size paper (8.5 x 11 in)</Typography>
    </Box>
  )

  const renderStep3Content = () => {
    if (!uspsLocationName && !uspsLocationAddress) return null

    return (
      <Box sx={styles.stepActionBlock}>
        <Box sx={styles.locationCard}>
          {uspsLocationName && <Typography sx={styles.locationNameText}>{uspsLocationName}</Typography>}

          {uspsLocationAddress && <Typography sx={styles.locationAddressText}>{uspsLocationAddress}</Typography>}

          {uspsLocationHours && (
            <Box sx={styles.locationMetaRow}>
              <Schedule sx={styles.locationMetaIcon} />
              <Typography sx={styles.locationMetaText}>{uspsLocationHours}</Typography>
            </Box>
          )}

          {uspsLocationAddress && (
            <Box sx={styles.locationMetaRow} onClick={handleGetDirections}>
              <Navigation sx={styles.directionsIcon} />
              <Typography sx={styles.directionsLinkText}>Get Directions</Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  const steps = [
    {
      stepNumber: 1,
      title: 'Print the USPS shipping label',
      description: 'Download and print the prepaid USPS label below',
      hasConnector: true,
      renderAction: renderStep1Content
    },
    {
      stepNumber: 2,
      title: 'Attach the label to the package',
      description: 'Securely tape the printed label onto the outside of the package',
      hasConnector: true,
      renderAction: null
    },
    {
      stepNumber: 3,
      title: 'Drop off at your nearest USPS location',
      description: 'Bring the labeled package to the USPS location below',
      hasConnector: false,
      renderAction: renderStep3Content
    }
  ]

  return (
    <Box sx={styles.container}>
      <Box sx={styles.stepsListContainer}>
        {steps.map((step) => (
          <Box key={step.stepNumber} sx={styles.stepContainer}>
            <Box sx={styles.stepIndicatorContainer}>
              <Box sx={styles.stepNumberCircle}>
                <Typography sx={styles.stepNumberText}>{step.stepNumber}</Typography>
              </Box>

              {step.hasConnector && <Box sx={styles.stepConnector} />}
            </Box>

            <Box sx={styles.stepContentContainer}>
              <Typography sx={styles.stepTitleText}>{step.title}</Typography>
              <Typography sx={styles.stepDescriptionText}>{step.description}</Typography>
              {step.renderAction?.()}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default DropoffDetailsModal
